import * as XLSX from "xlsx";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { ExcelDateToJSDate } from "app/utils/helper";
import {
  IDENTIFIER_SAMPLE_HEADERS,
  IDENTIFIER_SAMPLE_ROW,
  SUPPORT_BULK_UPDATE_FIELDS,
} from "./constants";

dayjs.extend(customParseFormat);

const IDENTIFIER_HEADER_MAP = {
  ADMISSIONNUMBER: "admissionNumber",
  STUDENTNAME: "studentName",
  CLASSSTREAM: "classStream",
  CLASS: "className",
  STREAM: "stream",
  SECTION: "section",
  FATHERNAME: "fatherName",
};

const FIELD_CONFIG_MAP = SUPPORT_BULK_UPDATE_FIELDS.reduce((acc, field) => {
  acc[field.key] = field;
  return acc;
}, {});

const normalizeCompareString = (value = "") =>
  String(value)
    .replace(/\r\n|\n|\r/g, " ")
    .replace(/_x000D_/gi, " ")
    .replace(/[^a-zA-Z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();

const mapBulkUpdateExcelHeader = (header = "") => {
  const key = normalizeCompareString(header).replace(/\s+/g, "");
  if (IDENTIFIER_HEADER_MAP[key]) {
    return IDENTIFIER_HEADER_MAP[key];
  }

  for (const field of SUPPORT_BULK_UPDATE_FIELDS) {
    if (field.excelHeaders.includes(key)) {
      return field.key;
    }
  }

  return null;
};

const parseExcelDateValue = (value) => {
  if (value === "" || value == null) return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return dayjs(value);
  }

  if (typeof value === "number") {
    const parsed = XLSX.SSF?.parse_date_code?.(value);
    if (parsed) {
      return dayjs(new Date(parsed.y, parsed.m - 1, parsed.d));
    }
    const jsDate = ExcelDateToJSDate(value);
    if (jsDate && !Number.isNaN(jsDate.getTime())) {
      return dayjs(jsDate);
    }
  }

  const formats = [
    "DD-MM-YYYY",
    "DD/MM/YYYY",
    "YYYY-MM-DD",
    "MM/DD/YYYY",
    "D-M-YYYY",
    "D/M/YYYY",
  ];

  for (const format of formats) {
    const parsed = dayjs(String(value).trim(), format, true);
    if (parsed.isValid()) return parsed;
  }

  const fallback = dayjs(String(value).trim());
  return fallback.isValid() ? fallback : null;
};

const validateFieldValue = (fieldKey, rawValue) => {
  const field = FIELD_CONFIG_MAP[fieldKey];
  if (!field) {
    return { valid: false, error: `Unknown field: ${fieldKey}` };
  }

  if (rawValue === "" || rawValue == null) {
    return { valid: true, value: null };
  }

  switch (field.type) {
    case "date": {
      const parsedDate = parseExcelDateValue(rawValue);
      if (!parsedDate) {
        return {
          valid: false,
          error: `${field.label} has invalid date format`,
        };
      }
      return { valid: true, value: parsedDate.format("YYYY-MM-DD") };
    }
    case "number": {
      const num = Number(rawValue);
      if (Number.isNaN(num)) {
        return {
          valid: false,
          error: `${field.label} must be a valid number`,
        };
      }
      return { valid: true, value: num };
    }
    case "aadhar": {
      const aadhar = String(rawValue).replace(/\D/g, "");
      if (aadhar.length !== 12) {
        return {
          valid: false,
          error: `${field.label} must be 12 digits`,
        };
      }
      return { valid: true, value: aadhar };
    }
    case "mobile": {
      const mobile = String(rawValue).replace(/\D/g, "");
      if (mobile.length !== 10) {
        return {
          valid: false,
          error: `${field.label} must be 10 digits`,
        };
      }
      return { valid: true, value: mobile };
    }
    default: {
      const text = String(rawValue).trim();
      if (!text) {
        return { valid: false, error: `${field.label} cannot be empty` };
      }
      return { valid: true, value: text };
    }
  }
};

export function readFileAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event.target.result);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsArrayBuffer(file);
  });
}

export function validateSupportBulkUpdateFile(fileBuffer, selectedFields = []) {
  const result = {
    isValid: false,
    fileErrors: [],
    rowErrors: [],
    validRowCount: 0,
    totalRowCount: 0,
  };

  if (!selectedFields.length) {
    result.fileErrors.push("Please select at least one field to update.");
    return result;
  }

  const invalidFields = selectedFields.filter((field) => !FIELD_CONFIG_MAP[field]);
  if (invalidFields.length) {
    result.fileErrors.push(`Invalid update fields: ${invalidFields.join(", ")}`);
    return result;
  }

  const workbook = XLSX.read(fileBuffer, { type: "array" });
  const sheetName = workbook.SheetNames[0];
  const rawRows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
    header: 1,
    defval: "",
    raw: true,
  });

  const headerRowIndex = rawRows.findIndex((row) =>
    row.some((cell) => normalizeCompareString(cell).includes("ADMISSIONNUMBER"))
  );

  if (headerRowIndex === -1) {
    result.fileErrors.push(
      "Invalid Excel format. Header row with AdmissionNumber column not found."
    );
    return result;
  }

  const headerRow = rawRows[headerRowIndex];
  const columnMap = {};
  headerRow.forEach((header, index) => {
    const mappedKey = mapBulkUpdateExcelHeader(header);
    if (mappedKey) {
      columnMap[mappedKey] = index;
    }
  });

  if (columnMap.admissionNumber === undefined) {
    result.fileErrors.push("Missing required column: AdmissionNumber");
    return result;
  }

  const missingUpdateColumns = selectedFields.filter(
    (field) => columnMap[field] === undefined
  );
  if (missingUpdateColumns.length) {
    const labels = missingUpdateColumns.map(
      (key) => FIELD_CONFIG_MAP[key]?.sampleHeader || key
    );
    result.fileErrors.push(`Missing update columns: ${labels.join(", ")}`);
    return result;
  }

  const seenAdmissionNumbers = new Set();

  for (let i = headerRowIndex + 1; i < rawRows.length; i++) {
    const row = rawRows[i];
    if (!row || !row.length) continue;

    const admissionNumberRaw = row[columnMap.admissionNumber];
    if (admissionNumberRaw === "" || admissionNumberRaw == null) {
      continue;
    }

    result.totalRowCount += 1;
    const rowIndex = i + 1;
    const errors = [];

    const admNum = Number(admissionNumberRaw);
    if (Number.isNaN(admNum)) {
      errors.push("AdmissionNumber must be a valid number");
    } else {
      const admissionKey = String(admNum);
      if (seenAdmissionNumbers.has(admissionKey)) {
        errors.push(`Duplicate AdmissionNumber in file: ${admNum}`);
      } else {
        seenAdmissionNumbers.add(admissionKey);
      }
    }

    const studentName =
      columnMap.studentName !== undefined
        ? String(row[columnMap.studentName] ?? "").trim()
        : "";

    let hasAtLeastOneUpdate = false;

    selectedFields.forEach((fieldKey) => {
      const rawValue = row[columnMap[fieldKey]];
      if (rawValue === "" || rawValue == null) return;

      hasAtLeastOneUpdate = true;
      const validation = validateFieldValue(fieldKey, rawValue);
      if (!validation.valid) {
        errors.push(validation.error);
      }
    });

    if (!hasAtLeastOneUpdate) {
      errors.push("At least one selected update field must have a value");
    }

    if (errors.length) {
      result.rowErrors.push({
        rowIndex,
        admissionNumber: admissionNumberRaw,
        studentName,
        errors,
      });
    } else {
      result.validRowCount += 1;
    }
  }

  if (!result.totalRowCount) {
    result.fileErrors.push("No student rows found in Excel file.");
    return result;
  }

  if (result.totalRowCount > 5000) {
    result.fileErrors.push("Maximum of 5000 records can be uploaded at once.");
    return result;
  }

  result.isValid =
    !result.fileErrors.length && !result.rowErrors.length && result.validRowCount > 0;

  return result;
}

export function buildSampleRows(selectedFieldKeys = []) {
  const selectedFields = SUPPORT_BULK_UPDATE_FIELDS.filter((field) =>
    selectedFieldKeys.includes(field.key)
  );

  const sampleRow = { ...IDENTIFIER_SAMPLE_ROW };
  selectedFields.forEach((field) => {
    sampleRow[field.sampleHeader] = field.sampleValue;
  });

  return [sampleRow];
}

export function downloadSupportBulkUpdateSample(selectedFieldKeys = []) {
  const sampleRows = buildSampleRows(selectedFieldKeys);
  const selectedFields = SUPPORT_BULK_UPDATE_FIELDS.filter((field) =>
    selectedFieldKeys.includes(field.key)
  );

  const headers = [
    ...IDENTIFIER_SAMPLE_HEADERS,
    ...selectedFields.map((field) => field.sampleHeader),
  ];

  const worksheet = XLSX.utils.json_to_sheet(sampleRows, { header: headers });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "SampleBulkUpdate");
  XLSX.writeFile(workbook, "SampleStudentBulkUpdate.xlsx");
}
